import React, { useState } from "react";
import {
  X,
  Sparkles,
  FileSpreadsheet,
  MoreVertical,
  FileText,
  Eye,
} from "lucide-react";
import {
  preBuiltTemplates,
  PreBuiltTemplate,
} from "../../data/preBuiltTemplates";

interface PreBuiltTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: PreBuiltTemplate) => void;
}

const PreBuiltTemplatesModal: React.FC<PreBuiltTemplatesModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleMenuToggle = (templateId: string) => {
    setOpenMenuId(openMenuId === templateId ? null : templateId);
  };

  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
  };

  // Use the imported preBuiltTemplates from our data file

  // Get unique categories from the actual templates
  const categories = [
    { value: "all", label: "All Templates" },
    ...Array.from(new Set(preBuiltTemplates.map((t) => t.category))).map(
      (category) => ({
        value: category.toLowerCase(),
        label: category.charAt(0).toUpperCase() + category.slice(1),
      })
    ),
  ];

  const filteredTemplates =
    selectedCategory === "all"
      ? preBuiltTemplates
      : preBuiltTemplates.filter(
          (template) => template.category.toLowerCase() === selectedCategory
        );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Pre-built Templates
                </h3>
                <p className="text-sm text-gray-500">
                  Choose from our collection of ready-to-use templates
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category.value
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-blue-500 p-2 rounded-lg shadow-sm">
                    <FileSpreadsheet className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <h4 className="font-medium text-gray-900 mb-1">
                        {template.name}
                      </h4>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuToggle(template.id);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="More options"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>

                        {openMenuId === template.id && (
                          <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                            <button
                              onClick={() =>
                                handleAction(() => onSelectTemplate(template))
                              }
                              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              Use Template
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {template.subject}
                    </p>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {template.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No templates found
              </h3>
              <p className="text-gray-500">
                No templates available for the selected category.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreBuiltTemplatesModal;
